import { DOCUMENT } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  signal,
  untracked,
} from '@angular/core';
import { Router } from '@angular/router';
import { GuideStep } from '../../models/guide.model';
import { AiGuideChatService } from '../../services/ai-guide-chat.service';
import { GuideCursorService } from '../../services/guide-cursor.service';
import { GuideEngineService, GuideNavDirection } from '../../services/guide-engine.service';
import { PosthogService } from '../../../services/analytics/posthog.service';
import { GuideTopLayerDirective } from '../../directives/guide-top-layer.directive';

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Padding around the highlighted element, in px. */
const SPOTLIGHT_PADDING = 8;
/** How long the synthetic cursor "travels" before a synthetic click fires. */
const CURSOR_TRAVEL_MS = 1500;
/** Shorter travel when the user drives a step manually via Next. */
const MANUAL_TRAVEL_MS = 450;
/** Auto-advance dwell for non-interactive steps. */
const OBSERVE_DWELL_MS = 2600;
/** Settle time after a smooth scroll before measuring / animating. */
const SCROLL_SETTLE_MS = 550;
/** Max time to wait for `waitForSelector` before giving up. */
const WAIT_TIMEOUT_MS = 8000;

/**
 * The visual engine. Reacts to `GuideEngineService.currentStep`, `isPaused` and
 * `navDirection` and, per step, spotlights the target element, moves the
 * synthetic cursor to it, and either performs the action (auto-play) or waits
 * (paused / review / user confirmation).
 *
 * Playback modes for a step becoming active:
 * - **playing** (forward, not paused) → perform action + auto-advance.
 * - **paused** (forward) → highlight only; wait for Play/Next.
 * - **review** (backward) → highlight only; no perform, no undo.
 * - **confirmation** step (`requiresUserConfirmation`) → always wait for the
 *   real user click, in any mode.
 * A step's mutating action is performed at most once (`performedIndices`).
 */
@Component({
  selector: 'app-guide-overlay',
  imports: [GuideTopLayerDirective],
  templateUrl: './guide-overlay.component.html',
  styleUrl: './guide-overlay.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '(window:resize)': 'reposition()',
    '(window:scroll)': 'reposition()',
  },
})
export class GuideOverlayComponent implements OnDestroy {
  private readonly engine = inject(GuideEngineService);
  private readonly chat = inject(AiGuideChatService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly posthog = inject(PosthogService);
  protected readonly cursor = inject(GuideCursorService);

  protected readonly spotlight = signal<SpotlightRect | null>(null);

  protected readonly step = this.engine.currentStep;
  protected readonly isActive = this.engine.isActive;
  protected readonly stepNumber = computed(() => this.engine.stepIndex() + 1);
  protected readonly totalSteps = this.engine.totalSteps;
  /** True while this step is waiting for the real user to act. */
  protected readonly awaitingUser = signal(false);

  /** The element the current step is anchored to (for repositioning). */
  private currentTarget: HTMLElement | null = null;
  /** Step indices whose mutating action has already been performed. */
  private readonly performedIndices = new Set<number>();
  /** Teardown callbacks for timers/listeners scoped to the current step. */
  private cleanups: Array<() => void> = [];
  /** Guards the initial `nextTick === 0` from firing a manual advance. */
  private lastNextTick = 0;

  constructor() {
    // Main playback effect: re-runs when the step, pause state or direction
    // changes. Clearing pending timers here also cancels a pending auto-advance
    // when the user pauses mid-step.
    effect(() => {
      const step = this.step();
      const paused = this.engine.isPaused();
      const direction = this.engine.navDirection();
      this.clearStepEffects();
      if (step) {
        void this.enterStep(step, paused, direction);
      } else {
        this.resetVisuals();
        this.performedIndices.clear();
      }
    });

    // Manual "Next": perform the current step (once, never a destructive
    // click) then advance. Reacts only to the tick.
    effect(() => {
      const tick = this.engine.nextTick();
      if (tick === this.lastNextTick) {
        return;
      }
      this.lastNextTick = tick;
      void this.onNextRequested();
    });
  }

  ngOnDestroy(): void {
    this.clearStepEffects();
  }

  private async enterStep(
    step: GuideStep,
    paused: boolean,
    direction: GuideNavDirection,
  ): Promise<void> {
    this.awaitingUser.set(false);
    const index = this.engine.stepIndex();

    // Navigate steps: perform navigation in every mode (benign, and required so
    // the next step's highlight target exists).
    if (step.action === 'navigate' && step.route) {
      if (!this.performedIndices.has(index)) {
        this.performedIndices.add(index);
        await this.router.navigateByUrl(step.route);
        if (step.waitForSelector) {
          await this.waitForSelector(step.waitForSelector);
        }
      }
      this.currentTarget = null;
      this.spotlight.set(null);
      this.cursor.hide();
      if (!paused && direction === 'forward') {
        this.advanceIfStill(step);
      }
      return;
    }

    // Wait for the step's own target to mount before resolving it — Flow A swaps
    // views on signals (no route change), so a target can appear a beat after a
    // prior action (e.g. the title input after a user-confirmed Continue).
    if (step.selector) {
      await this.waitForSelector(step.selector);
    }

    // Resolve + position the target.
    const target = step.selector ? this.query(step.selector) : null;
    if (step.selector && !target) {
      this.handleMissingTarget();
      return;
    }
    if (target) {
      this.currentTarget = target;
      target.scrollIntoView({ behavior: 'smooth', block: 'center' });
      await this.delay(SCROLL_SETTLE_MS);
      this.reposition();
    }

    // Destructive steps always wait for the real user click — in any mode.
    if (step.requiresUserConfirmation && target) {
      this.awaitingUser.set(true);
      this.waitForUserClick(target, step);
      return;
    }

    // Review (backward) or paused (forward): highlight only.
    if (direction === 'backward' || paused) {
      return;
    }

    // Playing forward: perform (once) + auto-advance.
    if (!this.performedIndices.has(index)) {
      await this.performAction(step, target, index, true);
    }
    this.advanceIfStill(step);
  }

  private async onNextRequested(): Promise<void> {
    const step = untracked(() => this.engine.currentStep());
    const index = untracked(() => this.engine.stepIndex());
    if (!step) {
      return;
    }

    // Perform the current step (unless already done or destructive), then move
    // on. Next never auto-fires a `requiresUserConfirmation` action.
    if (!this.performedIndices.has(index) && !step.requiresUserConfirmation) {
      const target = step.selector ? this.query(step.selector) : null;
      await this.performAction(step, target, index, false);
    }
    if (untracked(() => this.engine.currentStep())?.id === step.id) {
      this.engine.advance();
    }
  }

  /** Perform a step's mutating action exactly once. */
  private async performAction(
    step: GuideStep,
    target: HTMLElement | null,
    index: number,
    auto: boolean,
  ): Promise<void> {
    this.performedIndices.add(index);

    switch (step.action) {
      case 'type':
        if (target) {
          this.typeInto(target, step.inputValue ?? '');
        }
        if (auto) {
          await this.delay(OBSERVE_DWELL_MS);
        }
        break;

      case 'click':
        await this.delay(auto ? CURSOR_TRAVEL_MS : MANUAL_TRAVEL_MS);
        target?.click();
        if (step.waitForSelector) {
          await this.waitForSelector(step.waitForSelector);
        }
        break;

      case 'observe':
      default:
        if (auto) {
          await this.delay(OBSERVE_DWELL_MS);
        }
        break;
    }
  }

  /** Recompute the spotlight + cursor position from the current target's rect. */
  protected reposition(): void {
    const target = this.currentTarget;
    if (!target || !this.isActive()) {
      return;
    }
    const rect = target.getBoundingClientRect();
    this.spotlight.set({
      top: rect.top - SPOTLIGHT_PADDING,
      left: rect.left - SPOTLIGHT_PADDING,
      width: rect.width + SPOTLIGHT_PADDING * 2,
      height: rect.height + SPOTLIGHT_PADDING * 2,
    });
    this.cursor.moveTo(rect.left + rect.width / 2, rect.top + rect.height / 2);
  }

  private waitForUserClick(target: HTMLElement, step: GuideStep): void {
    const handler = (): void => {
      this.awaitingUser.set(false);
      this.advanceIfStill(step);
    };
    target.addEventListener('click', handler, { once: true });
    this.cleanups.push(() => target.removeEventListener('click', handler));
  }

  private typeInto(target: HTMLElement, value: string): void {
    if (
      target instanceof HTMLInputElement ||
      target instanceof HTMLTextAreaElement
    ) {
      target.focus();
      target.value = value;
      // Native `input`/`change` events so Angular Signal-Form `[field]` and
      // `[value]/(input)` handlers fire (this flow uses @angular/forms/signals,
      // so FormControl.setValue does not apply).
      target.dispatchEvent(new Event('input', { bubbles: true }));
      target.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  private handleMissingTarget(): void {
    const step = this.step();
    console.warn(
      '[ai-guide] step target not found on current screen; stopping walkthrough gracefully',
      { taskId: this.engine.activeTask()?.id, stepId: step?.id, selector: step?.selector },
    );
    this.posthog.posthog.capture('ai_guide_selector_missing', {
      taskId: this.engine.activeTask()?.id,
      stepId: step?.id,
      selector: step?.selector,
    });
    this.chat.narrate(
      "I can't find that on this screen right now — it may have moved or you're on a different page. Let's stop here; ask me again anytime.",
    );
    this.engine.cancel();
  }

  /** Advance only if the engine is still on the same step (guards against races). */
  private advanceIfStill(step: GuideStep): void {
    if (this.step()?.id === step.id) {
      this.engine.advance();
    }
  }

  private query(selector: string): HTMLElement | null {
    return this.document.querySelector<HTMLElement>(selector);
  }

  private waitForSelector(selector: string): Promise<HTMLElement | null> {
    const existing = this.query(selector);
    if (existing) {
      return Promise.resolve(existing);
    }
    return new Promise((resolve) => {
      const start = Date.now();
      const intervalId = setInterval(() => {
        const found = this.query(selector);
        if (found || Date.now() - start > WAIT_TIMEOUT_MS) {
          clearInterval(intervalId);
          resolve(found);
        }
      }, 100);
      this.cleanups.push(() => clearInterval(intervalId));
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      const id = setTimeout(resolve, ms);
      this.cleanups.push(() => clearTimeout(id));
    });
  }

  private clearStepEffects(): void {
    this.cleanups.forEach((fn) => fn());
    this.cleanups = [];
  }

  private resetVisuals(): void {
    this.currentTarget = null;
    this.spotlight.set(null);
    this.awaitingUser.set(false);
    this.cursor.hide();
  }
}
